import {
  deriveRangeSegments,
  formatDurationMinutes,
  type RangeSegment,
} from '@omecare/utils';
import { HeartHandshakeIcon } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useGetMeQuery } from '@/hooks/queries/use-me';
import type { OfficeAssistantPeriod } from '@/hooks/use-office-assistant-periods-for-slot';
import { TONES, toneBadgeClassName } from '@/lib/tones';
import { cn } from '@/lib/utils';
import { getInitials } from '@/utils/get-initials';

type Props = {
  /** The assistant periods to display (already computed by the caller). */
  periods: OfficeAssistantPeriod[];
};

/** One assistant's periods, collapsed into a single display row. */
type AssistantPeriodsGroup = {
  assistantId: string;
  userId: string;
  firstname: string;
  lastname: string;
  totalMinutes: number;
  /** The assistant's time ranges annotated with the pause preceding each. */
  segments: RangeSegment[];
};

/**
 * Groups the flat period list (one entry per time range) by assistant, so a
 * split shift renders as one row with stacked ranges and "pause" lines instead
 * of duplicating the assistant's identity per range. Preserves the incoming
 * (start-time) order of first appearance.
 */
function groupPeriodsByAssistant(
  periods: OfficeAssistantPeriod[],
): AssistantPeriodsGroup[] {
  const byAssistant = new Map<
    string,
    { identity: OfficeAssistantPeriod; ranges: OfficeAssistantPeriod[] }
  >();
  for (const period of periods) {
    const existing = byAssistant.get(period.assistantId);
    if (existing) {
      existing.ranges.push(period);
    } else {
      byAssistant.set(period.assistantId, {
        identity: period,
        ranges: [period],
      });
    }
  }

  const groups: AssistantPeriodsGroup[] = [];
  for (const { identity, ranges } of byAssistant.values()) {
    let totalMinutes = 0;
    for (const range of ranges) {
      totalMinutes += range.durationMinutes;
    }
    groups.push({
      assistantId: identity.assistantId,
      userId: identity.userId,
      firstname: identity.firstname,
      lastname: identity.lastname,
      totalMinutes,
      segments: deriveRangeSegments(ranges),
    });
  }
  return groups;
}

export const OfficeMedicalAssistantsSection = ({ periods }: Props) => {
  const { t } = useTranslation();
  const { data: me } = useGetMeQuery();

  const meUserId = me?.id ?? null;

  if (periods.length === 0) return null;

  const totalMinutes = periods.reduce((sum, p) => sum + p.durationMinutes, 0);
  const groups = groupPeriodsByAssistant(periods);

  return (
    <div className="flex flex-col gap-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-semibold text-sm">
          <HeartHandshakeIcon className={cn('size-4', TONES.emerald.text)} />
          {t('office-medical-assistants-section.title')}
        </h3>
        <span className="text-muted-foreground text-xs">
          {t('office-medical-assistants-section.total-duration', {
            duration: formatDuration(totalMinutes),
          })}
        </span>
      </div>

      <ul className="flex flex-col gap-2">
        {groups.map((group) => {
          const isMe = meUserId !== null && group.userId === meUserId;
          return (
            <li
              key={group.assistantId}
              className={cn(
                'flex items-center justify-between rounded-lg border bg-background px-3 py-2',
                isMe && `${TONES.emerald.surfaceBorder} ${TONES.emerald.pill}`,
              )}
            >
              <div className="flex items-center gap-3">
                <Avatar className="size-9">
                  <AvatarFallback
                    className={cn(
                      'font-medium text-xs',
                      TONES.emerald.pill,
                      TONES.emerald.text,
                    )}
                  >
                    {getInitials({
                      firstname: group.firstname,
                      lastname: group.lastname,
                    })}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 truncate font-medium text-sm">
                    <span>
                      {group.firstname} {group.lastname.toUpperCase()}
                    </span>
                    {isMe && (
                      <Badge
                        variant="outline"
                        className={cn(
                          'px-1.5 py-0 text-[10px]',
                          toneBadgeClassName(TONES.emerald),
                        )}
                      >
                        Moi
                      </Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground text-xs">
                    {formatDuration(group.totalMinutes)}
                  </div>
                </div>
              </div>
              <span className="flex flex-col gap-0.5 text-muted-foreground text-sm tabular-nums">
                {group.segments.map((segment) => (
                  <Fragment key={`${segment.startTime}-${segment.endTime}`}>
                    {segment.pauseBeforeMinutes !== null && (
                      <span className="text-xs">
                        ↳{' '}
                        {t('office-medical-assistants-section.pause', {
                          duration: formatDurationMinutes(
                            segment.pauseBeforeMinutes,
                          ),
                        })}
                      </span>
                    )}
                    <span>
                      {segment.startTime} — {segment.endTime}
                    </span>
                  </Fragment>
                ))}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

function formatDuration(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h > 0 && m > 0) return `${h}h${String(m).padStart(2, '0')}`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}
